import { useEffect, useState, useRef } from 'react';
import { Route, Routes, createSearchParams, useNavigate } from 'react-router';

import { Authorized } from '../../utility/auth';

import { Pages } from '../../types/pages';
import { CircularProgress } from '@mui/material';
import { failure, unauthorized } from '../../types/api';
import { Errors } from '../../types/errors';

import useMediaQuery from '@mui/material/useMediaQuery';
import SessionCards from './components/SessionCards';
import SearchBar from '../../components/searchBar';
import SessionInfo from '../../views/SessionInfo';

import apiFunctions from '../../api';

import '../../index.css';

const UserLookup = () => {
  let timerRef = useRef<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [cards, setCards] = useState([]);
  const sm = useMediaQuery('(min-width: 882px)');
  const navigate = useNavigate();

  useEffect(() => {
    let subscribed = true;

    let backSearch = sessionStorage.getItem('searchVal');
    if (backSearch) {
      setHasTyped(true);
      setSearchValue(backSearch);
      sessionStorage.removeItem('searchVal');
    }

    const searchValues = async (searchValue: string) => {
      if (searchValue === '' || searchValue === undefined || searchValue === null) {
        clearCards();
        setLoading(false);
      } else {
        await apiFunctions.apiPost('OnDemand/GetSessions', { SearchText: searchValue }).then((data) => {
          if (data === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.CallFailed })}`,
            });
          } else if (data === unauthorized) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.Unauthorized })}`,
            });
          }

          if (typeof data === 'string') {
            clearCards();
          } else {
            setCards(data);
          }
          setLoading(false);
        });
      }
    };

    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
    }
    if (hasTyped) {
      timerRef.current = setTimeout(async () => {
        if (subscribed) {
          setHasTyped(false);
          setLoading(true);
          clearCards();
          searchValues(searchValue);
        }
      }, 400);
    }

    return () => {
      subscribed = false;
    };
  }, [hasTyped, navigate, searchValue]);

  const clearCards = () => {
    setCards([]);
  };

  const onCardClick = (sessionUid: string) => {
    sessionStorage.setItem('searchVal', searchValue);

    navigate({
      pathname: '/sessioninfo',
      search: `?${createSearchParams({ id: sessionUid })}`,
    });
  };

  return (
    <div className="container">
      <SearchBar
        searchHeader="OnDemand Application Activity Search"
        searchLabel="Email, SalesforceId, AppUId, Business Name, Dba Name, Legal Name, or Location Name"
        searchTooltipHtml="User must enter a <b>complete</b> email, Salesforce ID, Application UID, DBA/Legal Name, or Location Name."
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        clearCards={clearCards}
        setHasTyped={setHasTyped}
        sm={sm}
      />
      <br></br>
      <div className="searchVal">
        {searchValue !== '' ? (
          <h3>
            Search Results for <span className="blueText">{searchValue}</span>
          </h3>
        ) : (
          ''
        )}
      </div>
      <div className="wrapper">
        <div className="cards">
          <div className="loading">{loading && <CircularProgress />}</div>
          {cards.length > 0 && <SessionCards cards={cards} onCardClick={onCardClick} />}
          {cards.length === 0 && !loading && <h2>No exact matches</h2>}
        </div>
      </div>

      <div className="resultTotal">{cards.length > 0 ? <h5>{cards.length} Result(s) Found</h5> : ''}</div>

      <Routes>
        <Route
          path={Pages.SessionInfo}
          element={
            <Authorized>
              <SessionInfo />
            </Authorized>
          }
        />
      </Routes>
    </div>
  );
};

export default UserLookup;
